import { FileSpreadsheet, Loader2 } from "lucide-react";
import { useState } from "react";
import { FileDropzone, FileItem } from "@/components/ui/FileDropzone";
import { Button } from "@/components/ui/button";
import { LoaderButton } from "@/components/ui/custom-buttons";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { toSupportedLocale } from "@/i18n";
import { useTranslation } from "react-i18next";
import { extractImportErrorMessage, useAnalyseImportFile } from "@/api/import";
import { translateImportApiError } from "@/lib/import/import-server-i18n";
import {
  IMPORT_FILE_ACCEPT,
  IMPORT_MAX_SIZE_MB,
  IMPORT_SLOW_SIZE_MB,
  ImportAnalyseResult,
  ImportTable,
} from "@/lib/import/import-types";

/**
 * Etape 3 : deposer le fichier rempli et l'analyser.
 *
 * Deux phases de progression **distinctes et etiquetees** : le transfert, puis
 * l'analyse. Un novice ne doit jamais voir une barre bloquee a 100 % sans
 * comprendre ce qui se passe.
 *
 * L'analyse n'ecrit rien : c'est ce que dit explicitement le texte de l'ecran.
 */
export const ImportStepUpload = ({
  table,
  onAnalysed,
  onBack,
}: {
  table: ImportTable;
  onAnalysed: (result: ImportAnalyseResult) => void;
  onBack: () => void;
}) => {
  const vt = useVisibleT();
  const { i18n, t } = useTranslation();
  const { toast } = useToast();

  // Langue d'interface : matching des en-têtes + labels figés dans le rapport.
  // Warnings / highlights / disclaimers sont des clés i18n retraduites au BO.
  // Sans `lang`, le serveur retombe sur la langue du modèle puis
  // `instance.mainLanguage`. Persistée comme `job.locale` pour les recalculs.
  const lang = toSupportedLocale(i18n.resolvedLanguage ?? i18n.language);

  const [file, setFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: analyse, isPending } = useAnalyseImportFile();

  /** Phase 2 : le transfert est fini, le serveur travaille. */
  const analysing = isPending && uploadPercent >= 100;

  const handleDrop = (files: File[]) => {
    const picked = files[0];
    if (!picked) return;
    setError(null);

    const sizeMb = picked.size / (1024 * 1024);
    if (sizeMb > IMPORT_MAX_SIZE_MB) {
      // Le plafond est concatene **hors** du texte traduit : `useVisibleT`
      // n'accepte pas d'options d'interpolation, donc une variable placee dans
      // le libelle de repli disparaitrait des que la cle serait remplie en base.
      setError(
        `${IMPORT_MAX_SIZE_MB} Mo — ${vt(
          "bo.import.upload.tooLarge",
          "ce fichier dépasse la taille maximale. Découpez-le en plusieurs fichiers.",
        )}`,
      );
      setFile(null);
      return;
    }
    setFile(picked);
  };

  const handleAnalyse = async () => {
    if (!file) return;
    setError(null);
    setUploadPercent(0);

    try {
      const result = await analyse({
        table,
        file,
        lang,
        onUploadProgress: setUploadPercent,
      });
      onAnalysed(result);
    } catch (caught) {
      const extracted = extractImportErrorMessage(caught);
      const fallback = vt(
        "bo.import.upload.analyseError",
        "Le fichier n'a pas pu être analysé.",
      );
      // Preferer `code` (+ params) : message figé FR ne suit pas le changement
      // de langue. Legacy : texte brut ou clé `bo.import.server.error.*`.
      const text = translateImportApiError(t, extracted, fallback);
      setError(text);
      toast({ variant: "destructive", title: text });
    }
  };

  const slow = file != null && file.size / (1024 * 1024) > IMPORT_SLOW_SIZE_MB;

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {!file && (
        <FileDropzone
          multiple={false}
          accept={IMPORT_FILE_ACCEPT}
          onDrop={handleDrop}
          onDropRejected={() =>
            setError(
              vt(
                "bo.import.upload.rejected",
                "Seuls les fichiers .xlsx et .xlsm sont acceptés. Si votre fichier est un .xls, ouvrez-le dans Excel et réenregistrez-le au format .xlsx.",
              ),
            )
          }
          // Repli sur l'extension : sous Windows le type MIME est parfois vide.
          validator={(candidate) =>
            /\.(xlsx|xlsm)$/i.test(candidate.name)
              ? null
              : { code: "invalid-extension", message: "Extension non acceptée" }
          }
        >
          {(isDragActive) => (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <FileSpreadsheet
                // Nuances brand definies dans tailwind.config.js : 50, 200, 300, 600, 700.
                className={isDragActive ? "w-8 h-8 text-brand-600" : "w-8 h-8 text-gray-400"}
                aria-hidden
              />
              <span className="font-medium">
                {vt(
                  "bo.import.upload.dropzone",
                  "Déposez votre fichier Excel ici, ou cliquez pour le choisir",
                )}
              </span>
              <span className="text-sm text-gray-500">
                {vt("bo.import.upload.accepted", "Formats acceptés : .xlsx, .xlsm")}
                {" · "}
                {vt("bo.import.upload.maxSize", "20 Mo maximum")}
              </span>
            </div>
          )}
        </FileDropzone>
      )}

      {file && (
        <div className="flex flex-col gap-3">
          <FileItem
            file={file}
            onDelete={
              isPending
                ? undefined
                : () => {
                    setFile(null);
                    setUploadPercent(0);
                    setError(null);
                  }
            }
          />
          {slow && !isPending && (
            <p className="text-sm text-amber-700">
              {vt(
                "bo.import.upload.slowHint",
                "Ce fichier est volumineux : l'analyse peut prendre une minute.",
              )}
            </p>
          )}
        </div>
      )}

      {isPending && (
        <div className="flex flex-col gap-2" aria-live="polite">
          {!analysing ? (
            <>
              <div className="flex justify-between text-sm">
                <span>{vt("bo.import.upload.transferring", "Transfert du fichier…")}</span>
                <span className="tabular-nums">{uploadPercent} %</span>
              </div>
              <Progress value={uploadPercent} className="h-2" />
            </>
          ) : (
            <>
              <span className="flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                {vt(
                  "bo.import.upload.analysing",
                  "Analyse de votre fichier… aucune fiche n'est modifiée.",
                )}
              </span>
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="flex flex-col gap-2 p-4 rounded-xl border border-red-300 bg-red-50 text-sm text-red-900"
        >
          <span>{error}</span>
          <Button
            variant="ghost"
            className="self-start px-0 hover:bg-transparent underline"
            onClick={() => {
              setFile(null);
              setError(null);
              setUploadPercent(0);
            }}
          >
            {vt("bo.import.upload.chooseAnother", "Choisir un autre fichier")}
          </Button>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} disabled={isPending}>
          {vt("bo.import.back", "Retour")}
        </Button>
        <LoaderButton
          isLoading={isPending}
          disabled={!file || isPending}
          onClick={handleAnalyse}
        >
          {vt("bo.import.upload.analyse", "Analyser mon fichier")}
        </LoaderButton>
      </div>
    </div>
  );
};
